/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect } from 'react';
import useProcedure from '../../../../procedure/useProcedure';
import { useConfiguration } from '../../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../../redux/enums/ConfigResourceProps';
import { useBioMaterials } from '../../../../redux/features/bioMaterials/useBioMaterials';
import { FormEntityStatus } from '../../../submission/constants';

const withBioAttachments = WrappedComponent => (props) => {
  const { formContext, idSchema, registry } = props;
  const { submissionId = {} } = formContext;

  const procedure = useProcedure();
  const {
    biologicalInfosKey,
    bioMaterialKey,
    attachmentsKey,
    currentBmAttachments,
    updateCurrentBmAttachmentsOnStore,
    currentBioMaterialId,
    getBioMaterialArrayIndex,
  } = useBioMaterials({ registry, submissionId, idSchema });

  useConfiguration(ConfigResourceProps.ATTACHMENT_CONFIGURATION, procedure.id);

  const onAttachmentUploaded = async (attachment) => {
    // add to the bioMaterial.attachments
    const updatedAttachments = [...currentBmAttachments, attachment];
    updateCurrentBmAttachmentsOnStore(updatedAttachments);

    const bioInfo = getBioMaterialArrayIndex();
    if (bioInfo !== undefined && bioInfo !== -1) {
      await registry.setFormData(
        updatedAttachments,
        `${biologicalInfosKey}.${bioInfo}.${bioMaterialKey}.${attachmentsKey}`,
      );
    }

    const allAttachments = registry.getFormData(attachmentsKey) ?? [];
    const updatedAllAttachments = [...allAttachments, attachment];
    await registry.setFormData(updatedAllAttachments, attachmentsKey);
  };

  const onUploadedAttachmentRemoved = async (attachment) => {
    // remove from the bioMaterial.attachments
    const updatedAttachments = currentBmAttachments.filter(a => a.id !== attachment.id);
    updateCurrentBmAttachmentsOnStore(updatedAttachments);
    const bioMaterialIndex = getBioMaterialArrayIndex();

    if (bioMaterialIndex !== undefined && bioMaterialIndex !== -1) {
      await registry.setFormData(
        updatedAttachments,
        `${biologicalInfosKey}.${bioMaterialIndex}.${bioMaterialKey}.${attachmentsKey}`,
      );
    }

    // remove from submission attachments
    const allAttachments = registry.getFormData(attachmentsKey);
    const updatedAllAttachments = allAttachments.filter(a => a.id !== attachment.id);
    await registry.setFormData(updatedAllAttachments, attachmentsKey);
  };

  useEffect(() => {
    // populate attachments in store with the attachments list from bio Material
    // if we edit a bio material we should have id
    // otherwise it is a new created id is undefined
    // unconfirmed entry will be loaded if user refreshed page while editing entry
    const biologicalInfos = registry.getFormData(biologicalInfosKey);
    const currentBiologicalInfo = biologicalInfos?.find(
      b =>
        b.id === currentBioMaterialId ||
        b.id === undefined ||
        (!currentBioMaterialId && b.state !== FormEntityStatus.CONFIRMED),
    );

    if (currentBiologicalInfo) {
      const { attachments = [] } = currentBiologicalInfo[bioMaterialKey];
      updateCurrentBmAttachmentsOnStore(attachments);
    }
  }, [currentBioMaterialId]);

  return (
    <>
      <WrappedComponent
        {...{
          ...props,
          attachmentsMetadata: currentBmAttachments,
          onAttachmentUploaded,
          onUploadedAttachmentRemoved,
        }}
      />
    </>
  );
};

export default withBioAttachments;
